// Import Firebase modules
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, OAuthProvider, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyDmJAitQgxv3bYC05sNk2Nd0SobrfnDZIU",
  authDomain: "autoinvent.app",
  projectId: "autoinvent-b4450",
  storageBucket: "autoinvent-b4450.firebasestorage.app",
  messagingSenderId: "649753956905",
  appId: "1:649753956905:web:bd27220e8740545e458238",
  measurementId: "G-9ETEV40P9J"
};

// Initialize Firebase - prevent duplicate initialization
let app;
if (!getApps().length) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApp();
}

// Initialize Firebase services immediately after app initialization
export const auth = getAuth(app);

// Explicitly set persistence so behavior is deterministic across browsers
// (Safari/private mode default-detection has historically been flaky).
setPersistence(auth, browserLocalPersistence).catch((err) => {
  console.warn('Could not set auth persistence:', err);
});

// Initialize Firestore with proper error handling
// The error "Service firestore is not available" means Firestore is not enabled in Firebase Console
let db = null;

// Wrap in an IIFE to ensure errors are caught
(function initializeFirestore() {
  try {
    db = getFirestore(app);
    // Verify Firestore is actually available
    if (db) {
      console.log('✅ Firestore initialized successfully');
    }
  } catch (error) {
    // Don't retry - the error means Firestore service is not available
    // This happens when Firestore Database is not enabled in Firebase Console
    console.error('❌ Firestore initialization failed:', error.message);
    console.warn('⚠️ Firestore is not available. To fix this:');
    console.warn('1. Go to https://console.firebase.google.com/');
    console.warn('2. Select your project: autoinvent-b4450');
    console.warn('3. Navigate to "Firestore Database" in the left sidebar');
    console.warn('4. Click "Create database" and follow the setup wizard');
    console.warn('5. Refresh this page after enabling Firestore');
    // Set db to null so components can check before using it
    db = null;
  }
})();

export { db };

// Initialize Firebase Storage with proper error handling
let storage = null;

(function initializeStorage() {
  try {
    storage = getStorage(app);
    if (storage) {
      console.log('✅ Firebase Storage initialized successfully');
    }
  } catch (error) {
    console.error('❌ Firebase Storage initialization failed:', error.message);
    console.warn('⚠️ Firebase Storage is not available. To fix this:');
    console.warn('1. Go to https://console.firebase.google.com/');
    console.warn('2. Select your project: autoinvent-b4450');
    console.warn('3. Navigate to "Storage" in the left sidebar');
    console.warn('4. Click "Get Started" and follow the setup wizard');
    console.warn('5. Refresh this page after enabling Storage');
    storage = null;
  }
})();

export { storage };

// Initialize Firebase Analytics separately and conditionally
let analytics = null;
if (typeof window !== 'undefined') {
  // Lazy load analytics to avoid blocking other services
  import('firebase/analytics').then(({ getAnalytics }) => {
    try {
      analytics = getAnalytics(app);
    } catch (error) {
      console.warn('Analytics could not be initialized:', error);
    }
  }).catch(() => {
    console.warn('Analytics module could not be loaded');
  });
}
export { analytics };

// Initialize OAuth providers
export const googleProvider = new GoogleAuthProvider();

// Configure OAuth providers
googleProvider.addScope('email');
googleProvider.addScope('profile');
googleProvider.setCustomParameters({ prompt: 'select_account' });

/*
 * Sign in with Apple. The iOS app offers it natively, and an account made there has no
 * password -- without this button on the web, those people had no way onto a laptop.
 *
 * Needs the web half configured in Firebase Console -> Authentication -> Apple: a Services
 * ID whose return URL is https://autoinvent.app/__/auth/handler, plus the Team ID, Key ID and
 * private key. The iOS app only needs the bundle ID, so it works without any of that and
 * the web does not.
 *
 * Apple sends the name once, on the first authorisation, and Firebase's web SDK does not
 * copy it onto the user -- CompleteProfileModal asks for it, as it does after Google.
 */
export const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');

export default app;
